import React, { useState, useEffect, useCallback, useRef } from 'react';
import LinkButton from '../LinkButton';
import QuestMap from '../QuestMap';
import PromptDiffModal from './PromptDiffModal';
import { ModelPicker, ModelTagRow, ModelsLegend, PromptLink } from './modelInfo';

// Auto-pipeline tab. Stage 0 (concepts) and stage 1 (route) run server-side via
// the claude CLI; their reasoning / web-search / answer trace streams here live
// (SSE) and is PERSISTED to the run, so it never vanishes — reopening a run
// replays the last generation's trace, raw output and result. Choosing a route
// creates a draft quest; stage 2 happens in the real editor.

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/pipeline${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

// Manual 24h format — toLocaleTimeString could fall back to a 12h clock.
const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Copy `text` to the clipboard, with a fallback for non-secure contexts.
async function copyText(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard) { await navigator.clipboard.writeText(text); return true; }
  } catch (e) { /* fall through to execCommand */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.left = '-999999px'; ta.style.top = '-999999px';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
}

// Small "Copy" button that flashes "✓ Copied" for a moment. `getText` is called
// lazily on click so it captures the latest content.
function CopyButton({ getText, label = 'Copy', style }) {
  const [copied, setCopied] = useState(false);
  const tRef = useRef(null);
  useEffect(() => () => { if (tRef.current) clearTimeout(tRef.current); }, []);
  const onClick = async () => {
    const ok = await copyText(typeof getText === 'function' ? getText() : getText);
    if (!ok) return;
    setCopied(true);
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button onClick={onClick} style={{ fontSize: 12, padding: '2px 8px', ...style }}>
      {copied ? '✓ Copied' : `📋 ${label}`}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Live trace panel. Renders an optional seed (a persisted trace) first, then —
// if `live` — subscribes to the run's SSE channel for the running slot. Text and
// thinking deltas grow in place (typewriter); tool/status lines carry a
// timestamp. Both seed and live are captured once at mount; the parent forces a
// fresh mount for each new generation via `key`.
// ----------------------------------------------------------------------------
function TracePanel({ runId, live, seed, onDone }) {
  const segsRef = useRef([]);
  const [, setTick] = useState(0);
  const rafRef = useRef(null);
  const boxRef = useRef(null);
  const doneRef = useRef(onDone); doneRef.current = onDone;
  const seedRef = useRef(seed);
  const liveRef = useRef(live);
  const seedingRef = useRef(false);

  const scheduleRender = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setTick((t) => t + 1);
      const el = boxRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const pushDelta = useCallback((kind, text) => {
    const segs = segsRef.current;
    const last = segs[segs.length - 1];
    if (last && last.kind === kind) last.text += text;
    else segs.push({ kind, text });
  }, []);

  const applyEvent = useCallback((ev) => {
    if (!ev || !ev.t) return;
    if (ev.t === 'thinking') pushDelta('thinking', ev.delta || '');
    else if (ev.t === 'text') pushDelta('text', ev.delta || '');
    else if (ev.t === 'tool') segsRef.current.push({ kind: 'tool', text: `🔎 ${ev.name}${ev.detail ? `: ${ev.detail}` : ''}`, ts: ev.ts });
    else if (ev.t === 'status') segsRef.current.push({ kind: 'status', text: ev.msg || '', ts: ev.ts });
    else if (ev.t === 'done') {
      // Primary message only — the diagnostic block already streamed in as its
      // own status event, so joining the whole array would print it twice.
      segsRef.current.push({ kind: 'status', text: ev.ok ? '✓ Done' : `✗ ${(ev.errors || [])[0] || 'error'}`, ts: ev.ts });
      if (!seedingRef.current && doneRef.current) doneRef.current(ev.ok, ev.errors);
    }
  }, [pushDelta]);

  useEffect(() => {
    segsRef.current = [];
    seedingRef.current = true;
    (seedRef.current || []).forEach(applyEvent);
    seedingRef.current = false;
    scheduleRender();
    if (!liveRef.current) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      // The run panel shows run-scoped generations (stages 0/1, batch); point
      // slots stream in parallel on the same channel and render in their modals.
      if (msg.type === 'pipeline_replay') {
        if (msg.pointUuid) return;
        segsRef.current = [];
        (msg.trace || []).forEach(applyEvent);
        scheduleRender();
      } else if (msg.type === 'pipeline_trace') {
        if (msg.ev && msg.ev.pointUuid) return;
        applyEvent(msg.ev);
        scheduleRender();
      }
    };
    es.onerror = () => { /* auto-reconnects; ignore */ };
    return () => es.close();
  }, [runId, applyEvent, scheduleRender]);

  const color = { thinking: '#8a8f98', text: 'inherit', tool: '#1a73e8', status: '#5f6368' };
  // Flatten the captured segments into plain text — the same content the box
  // renders, so "Copy trace" yields exactly what's on screen.
  const traceText = () => segsRef.current.map((s) => (
    (s.kind === 'tool' || s.kind === 'status')
      ? `\n${s.ts ? `[${fmtTime(s.ts)}] ` : ''}${s.text}\n`
      : s.text
  )).join('');
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <CopyButton getText={traceText} label="Copy trace" />
      </div>
      <div
        ref={boxRef}
        style={{
          height: 300, overflow: 'auto', background: 'rgba(127,127,127,0.06)',
          border: '1px solid var(--border,#ddd)', borderRadius: 6, padding: 10,
          fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
        }}
      >
        {segsRef.current.length === 0 && <span style={{ opacity: 0.5 }}>Waiting for trace…</span>}
        {segsRef.current.map((s, i) => (
          <span key={i} style={{ color: color[s.kind], fontStyle: s.kind === 'thinking' ? 'italic' : 'normal' }}>
            {(s.kind === 'tool' || s.kind === 'status')
              ? `\n${s.ts ? `[${fmtTime(s.ts)}] ` : ''}${s.text}\n`
              : s.text}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConceptCards({ concepts, onChoose, busy }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <CopyButton getText={() => JSON.stringify(concepts, null, 2)} label={`Copy all (${concepts.length})`} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, marginTop: 8 }}>
        {concepts.map((c) => (
          <div key={c.id} style={{ border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ fontWeight: 600 }}>{c.title}</div>
              <CopyButton getText={() => JSON.stringify(c, null, 2)} label="Copy" />
            </div>
            {c.area && <div style={{ fontSize: 12, opacity: 0.7 }}>{c.area}</div>}
            <div style={{ fontSize: 13, marginTop: 6 }}>{c.narrative_hook}</div>
            {Array.isArray(c.intrigue_questions) && c.intrigue_questions.length > 0 && (
              <ul style={{ fontSize: 12, margin: '8px 0 0 16px', opacity: 0.85 }}>
                {c.intrigue_questions.slice(0, 3).map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            )}
            {c.feasibility_risks && <div style={{ fontSize: 12, color: '#9a6700', marginTop: 6 }}>⚠ {c.feasibility_risks}</div>}
            <button style={{ marginTop: 10 }} disabled={busy} onClick={() => onChoose(c.id)}>Choose this concept</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VariantCard({ v, city, chosen, busy, onChoose, onOpen }) {
  const [showPoints, setShowPoints] = useState(false);
  const locs = v.locations || [];
  // Adapt skeleton points to what QuestMap expects (id, name, coordinates).
  const mapLocs = locs
    .filter((l) => Array.isArray(l.coordinates) && l.coordinates.length === 2)
    .map((l, i) => ({ id: i + 1, name: l.name, coordinates: l.coordinates, type: 'ordinary' }));

  return (
    <div style={{ border: chosen ? '2px solid #188038' : '1px solid var(--border,#ddd)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{v.label || v.id}{chosen && <span style={{ color: '#188038' }}> · ✓ chosen</span>}</div>
        <CopyButton getText={() => JSON.stringify(v, null, 2)} label="Copy" />
      </div>
      {/* Description on the left, route map on the right (stacks when narrow). */}
      <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {locs.length} pts · ~{v.estimated_total_walk_min} min walk · no-task {v.no_task_share != null ? v.no_task_share : v.view_only_share}
          </div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{v.narrative_summary}</div>
          {Array.isArray(v.compliance_flags) && v.compliance_flags.length > 0 && (
            <div style={{ fontSize: 12, color: '#9a6700', marginTop: 6 }}>⚠ {v.compliance_flags.join('; ')}</div>
          )}
        </div>
        {mapLocs.length > 0 && (
          <div style={{ flex: '1 1 360px', height: 280, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border,#eee)' }}>
            <QuestMap locations={mapLocs} selectedLocationId={null} onLocationSelect={() => {}} onLocationMove={() => {}} city={city} />
          </div>
        )}
      </div>

      {/* Full per-point detail Claude generated */}
      <details style={{ marginTop: 8 }} open={showPoints} onToggle={(e) => setShowPoints(e.target.open)}>
        <summary style={{ cursor: 'pointer', fontSize: 13 }}>Show every point's details ({locs.length})</summary>
        <ol style={{ fontSize: 12, margin: '8px 0 0 18px' }}>
          {locs.map((l, i) => (
            <li key={i} style={{ marginBottom: 8 }}>
              <strong>{l.name}</strong>{(l.no_task != null ? l.no_task : l.view_only) ? ' · no-task' : ''}{l.is_paid ? ' · 💰 paid' : ''}
              {Array.isArray(l.coordinates) && <span style={{ opacity: 0.6 }}> · [{l.coordinates.join(', ')}]</span>}
              {l.leg_to_next_min != null && <span style={{ opacity: 0.6 }}> · {l.leg_to_next_min} min to next</span>}
              {l.role_in_narrative && <div style={{ opacity: 0.85 }}>role: {l.role_in_narrative}</div>}
              {l.draft_task_idea && <div style={{ color: '#1a73e8' }}>task idea: {l.draft_task_idea}</div>}
              {l.access_notes && <div style={{ color: '#9a6700' }}>access: {l.access_notes}</div>}
              {l.transit_access && <div style={{ opacity: 0.7 }}>transit: {l.transit_access}</div>}
              {Array.isArray(l.sources) && l.sources.length > 0 && (
                <div style={{ opacity: 0.7 }}>sources: {l.sources.map((s, j) => (
                  <a key={j} href={s} target="_blank" rel="noreferrer" style={{ marginRight: 6 }}>[{j + 1}]</a>
                ))}</div>
              )}
            </li>
          ))}
        </ol>
      </details>

      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button disabled={busy} onClick={() => onChoose(v.id)}>{chosen ? 'Re-pick this route' : 'Choose this route'}</button>
        {chosen && onOpen && <button onClick={onOpen} style={{ background: '#188038', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4 }}>Open draft in editor →</button>}
      </div>
    </div>
  );
}

function VariantCards({ variants, city, chosenId, questId, onChoose, onOpen, busy }) {
  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CopyButton getText={() => JSON.stringify(variants, null, 2)} label={`Copy all (${variants.length})`} />
      </div>
      {variants.map((v) => (
        <VariantCard key={v.id} v={v} city={city} chosen={v.id === chosenId} busy={busy}
          onChoose={onChoose} onOpen={questId && onOpen ? () => onOpen(questId) : null} />
      ))}
    </div>
  );
}

function NewRunForm({ onCreated, onCancel }) {
  const [city, setCity] = useState('');
  const [theme, setTheme] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setError(null); setBusy(true);
    try {
      const run = await api('/runs/start', { method: 'POST', body: { city, theme } });
      onCreated(run);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>New quest</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ flex: '1 1 200px' }} placeholder="City (e.g. Tbilisi)" value={city} onChange={(e) => setCity(e.target.value)} />
        <input style={{ flex: '2 1 300px' }} placeholder="Theme (optional — otherwise the model proposes angles)" value={theme} onChange={(e) => setTheme(e.target.value)} />
      </div>
      {error && <div style={{ color: '#c5221f', fontSize: 13, marginTop: 6 }}>{error}</div>}
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <button onClick={create} disabled={busy || !city.trim()}>Create</button>
        <button onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

const PipelinePanel = ({ onClose, closeHref, onOpenQuest, initialRunId, onRunChange }) => {
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Generation trace state: which stage's trace is shown, whether it's running,
  // and a counter that forces a fresh TracePanel mount per generation.
  const [genNode, setGenNode] = useState(null);
  const [genActive, setGenActive] = useState(false);
  const [genCounter, setGenCounter] = useState(0);
  const [showPromptDiff, setShowPromptDiff] = useState(false);
  const [showModels, setShowModels] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRuns(await api('/runs')); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  // Deep link: open the run from the URL (/pipeline/<runId>) on first mount.
  useEffect(() => { if (initialRunId) openRun(initialRunId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openRun = async (id) => {
    setError(null);
    try {
      const r = await api(`/runs/${id}`);
      setRun(r);
      setGenActive(false);
      setGenCounter(0);
      // Show the last generation's persisted trace, if any.
      setGenNode(r.state && r.state.last_job ? r.state.last_job.node : null);
      if (onRunChange) onRunChange(id);
    } catch (e) { setError(e.message); }
  };
  const reloadRun = async () => {
    if (!run) return;
    try {
      const r = await api(`/runs/${run.id}`);
      setRun(r);
    } catch (e) { setError(e.message); }
  };
  const backToList = () => { setRun(null); setGenNode(null); setGenActive(false); if (onRunChange) onRunChange(null); };

  const deleteRun = async (id) => {
    if (!window.confirm('Delete this run?')) return;
    try { await api(`/runs/${id}`, { method: 'DELETE' }); if (run && run.id === id) backToList(); loadRuns(); } catch (e) { setError(e.message); }
  };

  const generate = async (node) => {
    setError(null);
    setGenNode(node);
    setGenActive(true);
    setGenCounter((c) => c + 1);
    try {
      await api(`/runs/${run.id}/generate`, { method: 'POST', body: { node } });
    } catch (e) { setError(e.message); setGenActive(false); }
  };
  const stopGeneration = async () => {
    try { await api(`/runs/${run.id}/stop`, { method: 'POST' }); } catch (e) { setError(e.message); }
  };
  const onTraceDone = async () => {
    setGenActive(false);
    await reloadRun(); // pull concepts / route_variants / last_job the job stored
  };

  const chooseConcept = async (conceptId) => {
    try { await api(`/runs/${run.id}/choose-concept`, { method: 'POST', body: { conceptId } }); await openRun(run.id); } catch (e) { setError(e.message); }
  };
  const chooseRoute = async (variantId) => {
    // Choosing is reversible: it creates/updates the draft quest but keeps the
    // run on the route view so you can compare or re-pick. Open the editor via
    // the "Open draft in editor" button.
    try {
      await api(`/runs/${run.id}/choose-route`, { method: 'POST', body: { variantId } });
      await reloadRun();
    } catch (e) { setError(e.message); }
  };
  // Open the draft quest in the editor, handing over its name + city so the
  // editor can show it in the dropdown under the right city filter.
  const openInEditor = (qid) => { if (onOpenQuest) onOpenQuest(qid, { quest_name: run.title, city: run.city }); };

  const wrap = { padding: 20, maxWidth: 980, margin: '0 auto', overflow: 'auto', height: '100%' };
  const st = run && run.state ? run.state : {};
  const lastJob = st.last_job || null;

  // Generation block for a stage: Stop/Generate button + (persisted or live) trace.
  // `modelSurface` is the registry key whose model this button will run.
  const renderGenerate = (node, generateLabel, regenLabel, modelSurface) => {
    const showingThisTrace = genNode === node;
    const seed = (!genActive && lastJob && lastJob.node === node) ? (lastJob.trace || []) : [];
    const hasResult = node === '0' ? Array.isArray(st.concepts) : Array.isArray(st.route_variants);
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {genActive && showingThisTrace
            ? <button onClick={stopGeneration} style={{ background: '#c5221f', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4 }}>■ Stop</button>
            : <button onClick={() => generate(node)}>{hasResult ? regenLabel : generateLabel}</button>}
          <ModelPicker k={modelSurface} />
          <PromptLink k={modelSurface} />
        </div>
        {showingThisTrace && (
          <TracePanel key={`${run.id}:${node}:${genCounter}`} runId={run.id} live={genActive} seed={seed} onDone={onTraceDone} />
        )}
        {showingThisTrace && !genActive && lastJob && lastJob.node === node && lastJob.raw && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: lastJob.status === 'error' ? '#c5221f' : '#5f6368' }}>
              {lastJob.status === 'error' ? 'Raw model output (not valid — retrievable here)' : 'Raw model output'}
            </summary>
            <div style={{ marginTop: 6 }}>
              <CopyButton getText={() => lastJob.raw} label="Copy raw output" />
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{lastJob.raw}</pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="pl-page" style={wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>🧩 Quest Pipeline {run ? `— ${run.title}` : ''}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowModels(true)} title="Which model runs which stage">⌁ Models</button>
          <button onClick={() => setShowPromptDiff(true)} title="View and diff prompt versions from git history">🔀 Compare prompts</button>
          {run && <button onClick={backToList}>← Runs</button>}
          <LinkButton className="native-button-link" href={closeHref} onClick={onClose}>Close</LinkButton>
        </div>
      </div>
      {showPromptDiff && <PromptDiffModal onClose={() => setShowPromptDiff(false)} />}
      {showModels && <ModelsLegend onClose={() => setShowModels(false)} />}
      {error && <div style={{ color: '#c5221f', marginTop: 10 }}>{error}</div>}

      {!run ? (
        <div style={{ marginTop: 16 }}>
          {showNew ? (
            <NewRunForm onCreated={(r) => { setShowNew(false); loadRuns(); openRun(r.id); }} onCancel={() => setShowNew(false)} />
          ) : (
            <button onClick={() => setShowNew(true)}>➕ New quest</button>
          )}
          {loading ? <div style={{ marginTop: 16 }}>Loading…</div> : (
            <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: 13, opacity: 0.7 }}>
                  <th style={{ padding: '6px 4px' }}>Title</th><th>City</th><th>Stage</th><th>Points</th><th>Updated</th><th></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border,#eee)' }}>
                    <td style={{ padding: '6px 4px' }}>
                      <span role="button" tabIndex={0} style={{ color: '#1a73e8', cursor: 'pointer' }}
                        onClick={() => openRun(r.id)} onKeyDown={(e) => { if (e.key === 'Enter') openRun(r.id); }}>{r.title}</span>
                    </td>
                    <td>{r.city || '—'}</td><td>{r.status}</td><td>{r.points_total}</td>
                    <td style={{ fontSize: 12, opacity: 0.7 }}>{new Date(r.updated_at).toLocaleString()}</td>
                    <td><button onClick={() => deleteRun(r.id)} title="Delete">🗑</button></td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={6} style={{ padding: 12, opacity: 0.6 }}>No runs yet.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {run.status === 'concept' && (
            <div>
              <h3>Stage 0 — route concepts</h3>
              <div style={{ fontSize: 13, opacity: 0.8 }}>City: {st.city}{st.theme ? ` · theme: ${st.theme}` : ' · no theme'}</div>
              {Array.isArray(st.concepts) && st.concepts.length > 0 && (
                <ConceptCards concepts={st.concepts} onChoose={chooseConcept} busy={genActive} />
              )}
              {renderGenerate('0', '✨ Generate concepts', '↻ Regenerate concepts', 'stage0')}
            </div>
          )}

          {run.status === 'route' && (
            <div>
              <h3>Stage 1 — route research</h3>
              {st.chosen_concept && <div style={{ fontSize: 13, opacity: 0.85, fontStyle: 'italic', marginBottom: 8 }}>Concept: {st.chosen_concept.title}</div>}
              {Array.isArray(st.route_variants) && st.route_variants.length > 0 && (
                <VariantCards
                  variants={st.route_variants}
                  city={run.city}
                  chosenId={st.chosen_variant_id}
                  questId={st.quest_id}
                  onChoose={chooseRoute}
                  onOpen={openInEditor}
                  busy={genActive}
                />
              )}
              {renderGenerate('1', '✨ Generate routes', '↻ Regenerate routes', 'stage1')}
            </div>
          )}

          {run.status === 'stage2' && (
            <div>
              <h3>Stage 2 — writing points</h3>
              <p style={{ fontSize: 13, opacity: 0.85 }}>
                A draft quest was created. Stage 2 (writing each point) happens in the normal editor —
                map, point list and the per-point Write / Revise buttons.
              </p>
              <div style={{ marginBottom: 10 }}>
                <ModelTagRow label="Stage 2 agents:" keys={['tres', 'tgen', 'tcheck', 'tfact', 'writer', 'critic', 'factcheck', 'formal']} />
              </div>
              {st.quest_id && onOpenQuest && <button onClick={() => openInEditor(st.quest_id)}>Open draft in editor →</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelinePanel;
export { TracePanel };
